/**
 * Typed API client. Same-origin by design: in dev Vite proxies /api and /health
 * to the backend (vite.config.ts), in production the backend serves this bundle.
 * No CORS, no base-URL juggling, no API key in the browser.
 */
import type {
  Accepted,
  AdminOverview,
  AdminRunRow,
  AuditEventRef,
  BzoIngestBody,
  Citation,
  CommittedPackDetail,
  DemoDossier,
  Disposition,
  DraftPack,
  DraftPackRef,
  DossierDeleted,
  DossierFileRef,
  DossierRef,
  DossierUploadBody,
  DossierUploaded,
  FactEdit,
  FactsView,
  GemeindePack,
  Health,
  InviteCreated,
  InviteRef,
  MemberRef,
  RecheckResult,
  RetentionOverview,
  RetentionSweepResult,
  RuleCheck,
  Run,
  RulebaseIngestion,
  RunDiff,
  RunReport,
  SessionInfo,
  VerificationQueueItem,
  WorkspaceDeleted,
  WorkspaceRef,
} from './types'

export const API_BASE = '/api/v1'

/**
 * One place that knows how a rulebase pack is addressed.
 *
 * Every rulebase route is workspace-scoped in the path, because a draft pack is
 * workspace-owned data: two prospect Büros can each ingest a Zollikon BZO, and
 * neither may read the other's. `zh/egg` becomes two path segments rather than
 * one encoded one — a `%2F` inside a segment survives neither every proxy nor
 * every history implementation intact.
 */
function packPath(workspaceId: string, packId: string): string {
  const parts = packId.split('/').map(encodeURIComponent).join('/')
  return `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/rulebase/packs/${parts}`
}

function ingestionsPath(workspaceId: string): string {
  return `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/rulebase/ingestions`
}

/**
 * What a human decided about ONE extracted item, against the cited source.
 *
 * `citation` is required, and there is no second form of this type without it.
 * That is the whole point of the verification gate: a confirmation that does not
 * record what it was checked against is worth nothing, so the UI is not able to
 * express one. `verifyZoneRow` re-checks it at runtime as well, because a
 * compile-time guarantee is not a guarantee against a `JSON.parse` somewhere.
 */
export interface ZoneVerifyItem {
  /** `ZONE_ROW_KEY` for the row itself, otherwise a parameter of that row. */
  key: string
  disposition: Disposition
  /** Required iff `corrected`; absent/null iff `unreadable` — never a zero. */
  value?: string | number | boolean | null
  citation: Citation
}

export interface RuleVerifyInput {
  disposition: Disposition | 'rejected'
  kind?: string | null
  /** A literal operand. There is no expression string anywhere in this API. */
  check?: RuleCheck | null
  artifact?: string | null
  citation: Citation
}

/** Thrown before the request when a confirmation would carry no citation. */
export class MissingCitationError extends Error {
  constructor(itemKey: string) {
    super(`Ohne Quellenangabe kann "${itemKey}" nicht bestätigt werden.`)
    this.name = 'MissingCitationError'
  }
}

/**
 * The server refuses a verification without a citation with 422; this refuses
 * it before the request is made, so the reason the reader gets names the item
 * rather than being a validation dump. Same rule on both sides of the seam:
 * a snippet of at least four characters and a page number.
 */
function assertCitation(itemKey: string, citation: Citation | null | undefined): void {
  const value = citation as { page?: number | null; snippet?: string } | null | undefined
  if (!value) throw new MissingCitationError(itemKey)
  if (typeof value.page !== 'number' || value.page < 1) throw new MissingCitationError(itemKey)
  if (typeof value.snippet !== 'string' || value.snippet.trim().length < 4) {
    throw new MissingCitationError(itemKey)
  }
}

/** What the upload screen hands to the intake endpoint. */
export interface DossierUploadInput {
  files: readonly File[]
  gemeinde: string
  bautyp: string
  verfahren: string
  /** Optional — the extractor reads it from the Baugesuch when it is empty. */
  zone: string
  title: string
  /** The FADP attestation. Never defaulted to true anywhere in this codebase. */
  attested: boolean
}

/**
 * CSRF: the backend sets a readable `bp_csrf` cookie next to the httpOnly
 * session cookie and compares the `X-CSRF-Token` header against the secret it
 * stored with the session. Every unsafe request has to echo it — a request
 * without the header is refused with 403 even though the session cookie rode
 * along, which is exactly the point.
 */
export const CSRF_COOKIE = 'bp_csrf'
export const CSRF_HEADER = 'X-CSRF-Token'

export function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)bp_csrf=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

function unsafeHeaders(): Record<string, string> {
  const token = csrfToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { [CSRF_HEADER]: token } : {}),
  }
}

/** CSRF header only — used for multipart, where the browser must set the
 *  Content-Type itself (it has to append the boundary). */
function unsafeHeadersNoContentType(): Record<string, string> {
  const token = csrfToken()
  return token ? { [CSRF_HEADER]: token } : {}
}

/** One place that knows how a dossier is addressed inside a workspace. */
function dossierPath(workspaceId: string, dossierId: string): string {
  return `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/dossiers/${encodeURIComponent(dossierId)}`
}

export class ApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * The global 401 seam.
 *
 * Every route except the three unauthenticated entry points requires a session,
 * and a session expires after 12 hours — so *any* call can come back 401 at any
 * time. Handling that per call site would mean forgetting it somewhere; instead
 * the client raises it once, and the session provider turns it into "signed
 * out, go to /anmelden, remember where you were".
 */
type UnauthorizedHandler = (info: { path: string }) => void

let unauthorizedHandler: UnauthorizedHandler | null = null

export function setUnauthorizedHandler(handler: UnauthorizedHandler): () => void {
  unauthorizedHandler = handler
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      ...init,
      // The session lives in an httpOnly cookie on the same origin.
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    })
  } catch (cause) {
    throw new ApiError(0, cause instanceof Error ? cause.message : 'Netzwerkfehler')
  }

  if (!response.ok) {
    const error = new ApiError(response.status, await readError(response))
    if (response.status === 401) unauthorizedHandler?.({ path })
    throw error
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** FastAPI puts the human-readable reason in `detail`. Never swallow it. */
async function readError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json()
    if (body && typeof body === 'object' && 'detail' in body) {
      const { detail } = body
      if (typeof detail === 'string') return detail
      return JSON.stringify(detail)
    }
  } catch {
    /* falls through to the status text */
  }
  return `${response.status} ${response.statusText}`.trim()
}

export const api = {
  health: (signal?: AbortSignal) => request<Health>('/health', { signal }),

  // --- auth (passwordless) -------------------------------------------------
  /** The signed-in account, or ApiError 401. Everything below needs it. */
  session: (signal?: AbortSignal) => request<SessionInfo>(`${API_BASE}/auth/session`, { signal }),

  /** Always 202, whether or not the address has an account. Never returns a link. */
  requestMagicLink: (email: string, signal?: AbortSignal) =>
    request<Accepted>(`${API_BASE}/auth/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      signal,
    }),

  /** Redeem the `?token=` from the mailed link. Single use, short-lived. */
  consumeMagicLink: (token: string, signal?: AbortSignal) =>
    request<SessionInfo>(`${API_BASE}/auth/magic-link/consume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      signal,
    }),

  /** Redeem an invite: creates the account and the membership, then signs in. */
  acceptInvite: (token: string, signal?: AbortSignal) =>
    request<SessionInfo>(`${API_BASE}/auth/invites/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      signal,
    }),

  signOut: (signal?: AbortSignal) =>
    request<void>(`${API_BASE}/auth/sign-out`, { method: 'POST', headers: unsafeHeaders(), signal }),

  // --- workspaces ----------------------------------------------------------
  workspaces: (signal?: AbortSignal) => request<WorkspaceRef[]>(`${API_BASE}/workspaces`, { signal }),

  /** Founder only — the API answers 403 for anyone else. */
  createWorkspace: (name: string, retentionDays?: number, signal?: AbortSignal) =>
    request<WorkspaceRef>(`${API_BASE}/workspaces`, {
      method: 'POST',
      headers: unsafeHeaders(),
      // Omitted rather than sent as null when the founder leaves it alone: the
      // server owns the default, and a client-side 30 would be a second copy of
      // it waiting to drift.
      body: JSON.stringify(retentionDays === undefined ? { name } : { name, retention_days: retentionDays }),
      signal,
    }),

  /**
   * Founder only. `live` is refused server-side with 403 + the reason while the
   * lock stands; the UI never offers it, and this call does not special-case it
   * — the server's answer is what the reader sees.
   */
  setDataMode: (id: string, dataMode: string, signal?: AbortSignal) =>
    request<WorkspaceRef>(`${API_BASE}/workspaces/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: unsafeHeaders(),
      body: JSON.stringify({ data_mode: dataMode }),
      signal,
    }),

  /**
   * Founder only (403 otherwise). Bounds are the server's — an out-of-range
   * value comes back as a 422 with the reason rather than being clamped here.
   *
   * Shortening the window re-dates the dossiers already stored; lengthening it
   * does not. That asymmetry lives in the server
   * (`app.retention.shorten_existing_windows`) and the UI states it next to the
   * control rather than reimplementing it.
   */
  setRetentionDays: (id: string, days: number, signal?: AbortSignal) =>
    request<WorkspaceRef>(`${API_BASE}/workspaces/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: unsafeHeaders(),
      body: JSON.stringify({ retention_days: days }),
      signal,
    }),

  workspace: (id: string, signal?: AbortSignal) =>
    request<WorkspaceRef>(`${API_BASE}/workspaces/${encodeURIComponent(id)}`, { signal }),

  members: (id: string, signal?: AbortSignal) =>
    request<MemberRef[]>(`${API_BASE}/workspaces/${encodeURIComponent(id)}/members`, { signal }),

  invites: (id: string, signal?: AbortSignal) =>
    request<InviteRef[]>(`${API_BASE}/workspaces/${encodeURIComponent(id)}/invites`, { signal }),

  /** The invite link comes back exactly once, here. It is not retrievable later. */
  createInvite: (id: string, email: string, role: 'admin' | 'member' = 'member', signal?: AbortSignal) =>
    request<InviteCreated>(`${API_BASE}/workspaces/${encodeURIComponent(id)}/invites`, {
      method: 'POST',
      headers: unsafeHeaders(),
      body: JSON.stringify({ email, role }),
      signal,
    }),

  revokeInvite: (workspaceId: string, inviteId: string, signal?: AbortSignal) =>
    request<InviteRef>(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/invites/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE', headers: unsafeHeaders(), signal },
    ),

  dossiers: (id: string, signal?: AbortSignal) =>
    request<DossierRef[]>(`${API_BASE}/workspaces/${encodeURIComponent(id)}/dossiers`, { signal }),

  /** Copy a built-in demo dossier into the workspace (audited as an upload). */
  adoptDemoDossier: (id: string, demoDossierId: string, signal?: AbortSignal) =>
    request<DossierRef>(`${API_BASE}/workspaces/${encodeURIComponent(id)}/dossiers/from-demo`, {
      method: 'POST',
      headers: unsafeHeaders(),
      body: JSON.stringify({ demo_dossier_id: demoDossierId }),
      signal,
    }),

  /**
   * ── THE UPLOAD CALL SITE ────────────────────────────────────────────────
   * This is the ONE place that names the intake endpoint's path and field
   * names, so following a change in that contract is an edit here and nowhere
   * else. Nothing about it is mocked or stubbed: if the endpoint is missing or
   * refuses, the server's own answer is what the upload screen shows.
   *
   *   POST /api/v1/workspaces/{id}/dossiers   (multipart/form-data)
   *   files          — one or more PDFs
   *   gemeinde, bautyp, verfahren, title
   *   attestation    — the FADP attestation; the server refuses with 422
   *                    before writing a byte when it is false
   *   start_run      — the run is started by the server, in the same request
   *   → 201 DossierUploaded { dossier, files, notices, run }
   */
  uploadDossier: (id: string, input: DossierUploadInput, signal?: AbortSignal) => {
    // `field()` types every name against the generated multipart body, so a
    // rename on the server breaks the build rather than a prospect's upload.
    const field = (name: keyof DossierUploadBody) => name
    const form = new FormData()
    for (const file of input.files) form.append(field('files'), file, file.name)
    form.append(field('gemeinde'), input.gemeinde)
    form.append(field('bautyp'), input.bautyp)
    form.append(field('verfahren'), input.verfahren)
    if (input.zone) form.append(field('zone'), input.zone)
    form.append(field('title'), input.title)
    form.append(field('attestation'), input.attested ? 'true' : 'false')
    form.append(field('start_run'), 'true')
    return request<DossierUploaded>(
      `${API_BASE}/workspaces/${encodeURIComponent(id)}/dossiers`,
      { method: 'POST', headers: unsafeHeadersNoContentType(), body: form, signal },
    )
  },

  audit: (id: string, signal?: AbortSignal) =>
    request<AuditEventRef[]>(`${API_BASE}/workspaces/${encodeURIComponent(id)}/audit`, { signal }),

  /** The trail of ONE dossier: uploads, runs, fact edits, exports — who, when. */
  dossierAudit: (workspaceId: string, dossierId: string, signal?: AbortSignal) =>
    request<AuditEventRef[]>(`${dossierPath(workspaceId, dossierId)}/audit`, { signal }),

  /**
   * ── THE DELETION CALL SITE ──────────────────────────────────────────────
   * Deleting is irreversible, so the confirmation is part of the request, not
   * only of the dialog: the server refuses with 422 unless `confirm` repeats
   * the dossier id. This function therefore cannot be called "by accident with
   * a default" — the id it sends is the id it deletes, and a UI that has not
   * shown the reader what will be destroyed has no business calling it.
   *
   *   DELETE /api/v1/workspaces/{id}/dossiers/{id}?confirm={dossier_id}
   *   → 200 DossierDeleted { files, bytes, runs, audit_redacted, … }
   */
  deleteDossier: (workspaceId: string, dossierId: string, signal?: AbortSignal) =>
    request<DossierDeleted>(
      `${dossierPath(workspaceId, dossierId)}?confirm=${encodeURIComponent(dossierId)}`,
      { method: 'DELETE', headers: unsafeHeaders(), signal },
    ),

  // --- M5: the founder dashboard (403 for everyone else) -------------------
  adminOverview: (signal?: AbortSignal) =>
    request<AdminOverview>(`${API_BASE}/admin/overview`, { signal }),

  adminRuns: (limit = 25, signal?: AbortSignal) =>
    request<AdminRunRow[]>(`${API_BASE}/admin/runs?limit=${limit}`, { signal }),

  adminVerificationQueue: (signal?: AbortSignal) =>
    request<VerificationQueueItem[]>(`${API_BASE}/admin/verification-queue`, { signal }),

  /** Events with no workspace: founder sign-ins and retention sweeps. */
  adminSystemAudit: (limit = 50, signal?: AbortSignal) =>
    request<AuditEventRef[]>(`${API_BASE}/admin/audit?limit=${limit}`, { signal }),

  adminRetention: (signal?: AbortSignal) =>
    request<RetentionOverview>(`${API_BASE}/admin/retention`, { signal }),

  /** `dryRun` counts what is due and deletes nothing. */
  adminSweepRetention: (dryRun = false, signal?: AbortSignal) =>
    request<RetentionSweepResult>(
      `${API_BASE}/admin/retention/sweep?dry_run=${dryRun ? 'true' : 'false'}`,
      { method: 'POST', headers: unsafeHeaders(), signal },
    ),

  /**
   * Delete a whole workspace. The server wants the workspace's **slug** in
   * `confirm` — the "type the name to confirm" step lives in the contract, so
   * the caller has to have asked a human for it.
   */
  deleteWorkspace: (workspaceId: string, confirmSlug: string, signal?: AbortSignal) =>
    request<WorkspaceDeleted>(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}?confirm=${encodeURIComponent(confirmSlug)}`,
      { method: 'DELETE', headers: unsafeHeaders(), signal },
    ),

  // --- facts review: read, correct, re-check ------------------------------
  /** Metadata of the uploaded PDFs — never their content. */
  /** One dossier, including the date its retention window closes. */
  dossier: (workspaceId: string, dossierId: string, signal?: AbortSignal) =>
    request<DossierRef>(dossierPath(workspaceId, dossierId), { signal }),

  dossierFiles: (workspaceId: string, dossierId: string, signal?: AbortSignal) =>
    request<DossierFileRef[]>(`${dossierPath(workspaceId, dossierId)}/files`, { signal }),

  /** Every extracted fact with provenance + the "fehlende Eingaben" checklist. */
  facts: (workspaceId: string, dossierId: string, signal?: AbortSignal) =>
    request<FactsView>(`${dossierPath(workspaceId, dossierId)}/facts`, { signal }),

  /**
   * A human correction. The server flips the field's provenance to `manuell`
   * and records old → new in the audit trail; it validates the *type* only —
   * whether a value passes a rule is the engine's business, never the UI's.
   */
  editFacts: (
    workspaceId: string,
    dossierId: string,
    edits: readonly FactEdit[],
    signal?: AbortSignal,
  ) =>
    request<FactsView>(`${dossierPath(workspaceId, dossierId)}/facts`, {
      method: 'PATCH',
      headers: unsafeHeaders(),
      body: JSON.stringify({ edits }),
      signal,
    }),

  /**
   * The correction loop's second half: a synchronous deterministic re-check.
   * No model is called — the server measures this in tens of milliseconds, and
   * the answer already carries the run, the report AND the diff, so the UI
   * needs exactly one round trip to show what the correction changed.
   */
  recheck: (workspaceId: string, dossierId: string, signal?: AbortSignal) =>
    request<RecheckResult>(`${dossierPath(workspaceId, dossierId)}/recheck`, {
      method: 'POST',
      headers: unsafeHeaders(),
      signal,
    }),

  // --- runs ----------------------------------------------------------------
  demoDossiers: (signal?: AbortSignal) => request<DemoDossier[]>(`${API_BASE}/demo-dossiers`, { signal }),

  /**
   * Every pack visible to the caller: the repo packs plus draft packs.
   * `workspaceId` narrows the drafts to one workspace — without it a member
   * sees their own, a founder sees all of them.
   */
  /**
   * Every zone row and every effective rule of a COMMITTED pack — read-only.
   *
   * Global, not workspace-scoped: a committed pack is repo data
   * (`rulebase/zh/<gemeinde>/`), the same for every workspace, and the route
   * has no writer. `kanton`/`gemeinde` are the two halves of `pack_id`
   * (`zh/egg`), two segments for the same reason `packPath` uses two. 404 for a
   * draft: those are read through the workspace's own rulebase routes.
   */
  committedPack: (kanton: string, gemeinde: string, signal?: AbortSignal) =>
    request<CommittedPackDetail>(
      `${API_BASE}/gemeinden/${encodeURIComponent(kanton)}/${encodeURIComponent(gemeinde)}`,
      { signal },
    ),

  gemeinden: (workspaceId?: string, signal?: AbortSignal) =>
    request<GemeindePack[]>(
      `${API_BASE}/gemeinden` +
        (workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : ''),
      { signal },
    ),

  createRun: (dossierId: string, workspaceId?: string, signal?: AbortSignal) =>
    request<Run>(`${API_BASE}/runs`, {
      method: 'POST',
      headers: unsafeHeaders(),
      body: JSON.stringify(
        workspaceId ? { dossier_id: dossierId, workspace_id: workspaceId } : { dossier_id: dossierId },
      ),
      signal,
    }),

  run: (runId: string, signal?: AbortSignal) => request<Run>(`${API_BASE}/runs/${encodeURIComponent(runId)}`, { signal }),

  report: (runId: string, signal?: AbortSignal) =>
    request<RunReport>(`${API_BASE}/runs/${encodeURIComponent(runId)}/report`, { signal }),

  /**
   * What changed against the run this one was queued after. `against` overrides
   * the comparison partner; without it the server uses `parent_run_id`, which
   * is recorded on the run itself — so the answer does not drift with whatever
   * has been started since.
   */
  runDiff: (runId: string, against?: string, signal?: AbortSignal) =>
    request<RunDiff>(
      `${API_BASE}/runs/${encodeURIComponent(runId)}/diff` +
        (against ? `?against=${encodeURIComponent(against)}` : ''),
      { signal },
    ),

  // --- M4: the rulebase manager -------------------------------------------
  // ── THE RULEBASE CALL SITES ───────────────────────────────────────────────
  // Every path this milestone talks to is named here and nowhere else, so
  // following a change in the backend contract is an edit in this block. None
  // of it is stubbed: if an endpoint is missing, the server's own 404 is what
  // the surface shows. See decisions.md (M4) for which of these were live at
  // hand-off time.

  /**
   * Ingest a BZO for a Gemeinde that has no pack. The result is a DRAFT pack:
   * data only, nothing verified, no executable expression anywhere in it.
   * The server starts the work and answers immediately — the UI polls.
   *
   *   POST /api/v1/rulebase/ingestions   (multipart/form-data)
   *   file, gemeinde, kanton
   *   → 202 RulebaseIngestion { id, status, stages[], pack_id, notices[] }
   */
  ingestBzo: (
    workspaceId: string,
    input: {
      file: File
      gemeinde: string
      kanton: string
      /** «öffentliches Gemeindedokument, keine Personendaten» — never defaulted. */
      attested: boolean
      replace?: boolean
    },
    signal?: AbortSignal,
  ) => {
    const field = (name: keyof BzoIngestBody) => name
    const form = new FormData()
    form.append(field('file'), input.file, input.file.name)
    form.append(field('gemeinde'), input.gemeinde)
    form.append(field('kanton'), input.kanton)
    form.append(field('attestation'), input.attested ? 'true' : 'false')
    // Only sent when the reader has answered the 409 — never defaulted to true.
    if (input.replace) form.append(field('replace'), 'true')
    return request<RulebaseIngestion>(ingestionsPath(workspaceId), {
      method: 'POST',
      headers: unsafeHeadersNoContentType(),
      body: form,
      signal,
    })
  },

  /** Poll target for the ingestion progress view. Real stages, real reasons. */
  ingestion: (workspaceId: string, id: string, signal?: AbortSignal) =>
    request<RulebaseIngestion>(
      `${ingestionsPath(workspaceId)}/${encodeURIComponent(id)}`,
      { signal },
    ),

  ingestions: (workspaceId: string, signal?: AbortSignal) =>
    request<RulebaseIngestion[]>(ingestionsPath(workspaceId), { signal }),

  /**
   * The workspace's own draft packs, including the shadowed ones that
   * `GET /gemeinden` deliberately leaves out (a Gemeinde that already has a
   * committed pack keeps that row, because the committed pack is what runs).
   */
  draftPacks: (workspaceId: string, signal?: AbortSignal) =>
    request<DraftPackRef[]>(
      `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/rulebase/packs`,
      { signal },
    ),

  /** The draft pack behind the verification UI: zones, rules, and citations. */
  draftPack: (workspaceId: string, packId: string, signal?: AbortSignal) =>
    request<DraftPack>(packPath(workspaceId, packId), { signal }),

  /**
   * Verify ONE zone row — the largest unit the brief allows to be confirmed in
   * one act, because one zone row is one row of the BZO's parameter table and
   * a human can check it in one look. There is deliberately no call that
   * verifies a whole pack; the server refuses that too.
   *
   * Every item carries its citation, and the loop below refuses locally before
   * a request is ever made.
   */
  verifyZoneRow: (
    workspaceId: string,
    packId: string,
    zoneId: string,
    items: readonly ZoneVerifyItem[],
    signal?: AbortSignal,
  ) => {
    for (const item of items) assertCitation(item.key, item.citation)
    return request<DraftPack>(
      `${packPath(workspaceId, packId)}/zones/${encodeURIComponent(zoneId)}/verify`,
      { method: 'POST', headers: unsafeHeaders(), body: JSON.stringify({ items }), signal },
    )
  },

  /**
   * Verify one candidate rule. A `computable` rule becomes verified only with
   * an operator and a literal operand a human read off the cited source — the
   * ingestion never produces one, and there is no expression string to send.
   */
  verifyRule: (
    workspaceId: string,
    packId: string,
    ruleId: string,
    input: RuleVerifyInput,
    signal?: AbortSignal,
  ) => {
    assertCitation(ruleId, input.citation)
    return request<DraftPack>(
      `${packPath(workspaceId, packId)}/rules/${encodeURIComponent(ruleId)}/verify`,
      { method: 'POST', headers: unsafeHeaders(), body: JSON.stringify(input), signal },
    )
  },
}

/**
 * The draft pack as YAML, for the founder who commits it into `rulebase/`
 * through the ordinary git flow. The server never writes into the repo tree —
 * the diff stays reviewable, which is the property that makes packs data.
 */
export function packExportHref(
  workspaceId: string,
  packId: string,
  format: 'yaml' | 'zip' = 'yaml',
): string {
  return `${packPath(workspaceId, packId)}/export?format=${format}`
}

/**
 * Where the cited page can be read, or `null` when it cannot.
 *
 * `source_url` is the backend's answer and already carries the `#page=`
 * fragment. It is null for the repo packs, whose PDFs are not served — and in
 * that case this returns null too, so the UI shows the snippet and the page
 * reference instead of a link that would 404. A dead link on a verification
 * screen is worse than no link: it teaches the reader to stop clicking.
 */
export function sourcePageHref(citation: Citation): string | null {
  const c = citation as unknown as { source_url?: string | null }
  return c.source_url ?? null
}

export type Api = typeof api
